import i18n from "i18next";
/**
 * What you did recently, per tool — for Home's cards and the Lilia menu's
 * Recent (navigation handoff §2, §3).
 *
 * The API answers `GET /api/recent`: per tool, your own recent items, each
 * with its dates and, for documents, the last action the audit log recorded
 * (created, imported, exported, restored). Activity means **your** acts; other
 * people's edits belong to Sharing and notifications.
 *
 * `activityLabel` turns an item into words, and is the one place the rule
 * lives (Olivia, 27 Sep):
 *   - a logged action says what it was: "imported from .docx";
 *   - everything else is "edited" — never "updated";
 *   - tables add "in N papers", which the API counts;
 *   - never guess an action from what changed (no "added 3 rows"): a wrong
 *     word is worse than a plain one.
 */
import { useQuery } from "@tanstack/react-query";
import type { DocumentLook } from "@/lib/api";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { paths } from "./addresses";

export interface RecentItem {
  id: string;
  title: string;
  /** When you last did something to it: what orders the list. */
  at: string;
  updatedAt?: string | null;
  /** The last logged action on it, e.g. `document.import`. */
  action?: string | null;
  actionAt?: string | null;
  /** The file format the action was about: `docx`, `latex`, `pdf`… */
  actionFormat?: string | null;
  /** The paper it lives in or became (references, imports). */
  documentId?: string | null;
  /** Tables: how many papers use it. */
  documentCount?: number | null;
  /** Documents: the theme, when the API sends it (else Home reads the documents list). */
  look?: DocumentLook | null;
}

export interface RecentTool {
  tool: string;
  lastUsedAt: string;
  items: RecentItem[];
}

// ── words ────────────────────────────────────────────────────────────────────

const EXT: Record<string, string> = { latex: "tex", tex: "tex", markdown: "md", md: "md", word: "docx" };
const ext = (format?: string | null) => {
  if (!format) return null;
  const f = format.toLowerCase().replace(/^\./, "");
  return `.${EXT[f] ?? f}`;
};

/** A logged action, in words. Null for an action that says nothing about the
 *  item's content (hide, delete…) or that we don't know: those read "edited". */
export function actionWords(action?: string | null, format?: string | null): string | null {
  switch (action) {
    case "document.create": return i18n.t("shell:activity.created");
    case "document.import": return ext(format) ? i18n.t("shell:activity.importedFrom", { ext: ext(format) }) : i18n.t("shell:activity.imported");
    case "document.export": return ext(format) ? i18n.t("shell:activity.exportedTo", { ext: ext(format) }) : i18n.t("shell:activity.exported");
    case "document.restore": return i18n.t("shell:activity.restored");
    case "document.public.copy": return i18n.t("shell:activity.copied");
    case "template.create": return i18n.t("shell:activity.savedTemplate");
    default: return null;
  }
}

/** A logged action older than the last edit no longer describes the item. The
 *  slack covers an import whose blocks land a little after it's logged. */
const ACTION_SLACK_MS = 2 * 60 * 1000;

const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : NaN);

/**
 * What a row says: the act, in words, and when.
 *
 *   { what: "imported from .docx", at: <actionAt> }
 *   { what: "edited · in 4 papers", at: <updatedAt> }
 */
export function activityLabel(tool: string, item: RecentItem): { what: string; at: string } {
  const words = actionWords(item.action, item.actionFormat);
  const actionAt = time(item.actionAt);
  const updatedAt = time(item.updatedAt);
  const actionIsLatest = words && !Number.isNaN(actionAt) && (Number.isNaN(updatedAt) || actionAt >= updatedAt - ACTION_SLACK_MS);

  let what = actionIsLatest ? words! : i18n.t("shell:activity.edited");
  const at = (actionIsLatest ? item.actionAt : item.updatedAt) ?? item.at;

  const n = item.documentCount ?? 0;
  if (tool === "tables" && n > 0) what += ` · ${i18n.t("shell:activity.inPapers", { count: n })}`;
  return { what, at };
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * When, relative to now.
 *
 *   long:  "just now", "20 min ago", "2 h ago", "yesterday", "Tue", "12 Sep"
 *   short: "now",      "20 min",     "2 h",     "yesterday", "Tue", "12 Sep"
 */
export function relWhen(iso: string, opts: { short?: boolean; now?: Date } = {}): string {
  const now = opts.now ?? new Date();
  const then = new Date(iso);
  const s = Math.max(0, (now.getTime() - then.getTime()) / 1000);
  const w = (key: string, n?: number) => i18n.t(`shell:when.${key}`, { n });
  if (s < 60) return w(opts.short ? "now" : "justNow");
  if (s < 3600) return w(opts.short ? "minShort" : "min", Math.floor(s / 60));
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 86_400_000;
  if (then.getTime() >= startOfToday) return w(opts.short ? "hShort" : "h", Math.floor(s / 3600));
  if (then.getTime() >= startOfToday - day) return w("yesterday");
  const lang = i18n.language ?? "en";
  if (lang.startsWith("fr") || lang.startsWith("es")) {
    const locale = lang.startsWith("fr") ? "fr-FR" : "es-ES";
    if (then.getTime() >= startOfToday - 6 * day) return then.toLocaleDateString(locale, { weekday: "short" });
    return then.toLocaleDateString(locale, { day: "numeric", month: "short" });
  }
  if (then.getTime() >= startOfToday - 6 * day) return DAYS[then.getDay()];
  // Spelled out here: ICU's short months vary ("Sep", "Sept") by version.
  return `${then.getDate()} ${MONTHS[then.getMonth()]}`;
}

// ── where a row goes ─────────────────────────────────────────────────────────

/** Opening a row goes straight into the item, where it has an address. */
export function itemHref(tool: string, item: RecentItem): string {
  switch (tool) {
    case "documents": return paths.doc(item.id);
    case "tables": return paths.table(item.id);
    case "references": return item.documentId ? paths.doc(item.documentId) : paths.references;
    case "import": return item.documentId ? paths.doc(item.documentId) : `${paths.import}?tab=history`;
    case "templates": return paths.templates;
    case "equations": return paths.equations;
    case "snippets": return paths.snippets;
    default: return paths.home;
  }
}

/** Every tool's items in one list, newest first: the Lilia menu's Recent. */
export function acrossTools(tools: RecentTool[], limit: number): (RecentItem & { tool: string })[] {
  return tools
    .flatMap((t) => t.items.map((i) => ({ ...i, tool: t.tool })))
    .sort((a, b) => time(b.at) - time(a.at))
    .slice(0, limit);
}

// ── data ─────────────────────────────────────────────────────────────────────

/**
 * Your recent activity, per tool. Null while loading; an empty list for a
 * first day. If the API is older than /api/recent, falls back to your
 * documents by their dates — Home still works, with "edited" for every row.
 */
export function useRecent(perTool = 3, enabled = true) {
  const { fetchWithAuth } = useApiContext();
  return useQuery({
    queryKey: ["recent", perTool],
    enabled,
    staleTime: 15_000,
    queryFn: async (): Promise<RecentTool[]> => {
      const res = await fetchWithAuth(`${API_BASE}/recent?perTool=${perTool}`);
      if (res.ok) return ((await res.json()) as { tools: RecentTool[] }).tools ?? [];
      if (res.status !== 404) throw new Error(`Could not load recent activity (${res.status})`);
      const docs = await fetchWithAuth(`${API_BASE}/documents?page=1&pageSize=${perTool}&sortBy=updatedAt&sortDir=desc`);
      if (!docs.ok) throw new Error(`Could not load recent activity (${docs.status})`);
      const { items } = (await docs.json()) as { items: { id: string; title?: string; updatedAt: string; lastOpenedAt?: string; role?: string; look?: DocumentLook | null }[] };
      const own = items.filter((d) => !d.role || d.role.toLowerCase() === "owner");
      if (own.length === 0) return [];
      const at = (d: (typeof own)[number]) => (d.lastOpenedAt && d.lastOpenedAt > d.updatedAt ? d.lastOpenedAt : d.updatedAt);
      return [{
        tool: "documents",
        lastUsedAt: at(own[0]),
        items: own.map((d) => ({ id: d.id, title: d.title || i18n.t("documents:library.card.untitled"), at: at(d), updatedAt: d.updatedAt, look: d.look ?? null })),
      }];
    },
  });
}
